<script setup>
import { computed, ref } from 'vue'

const props = defineProps({
  goldenDays: {
    type: [Array, Set],
    default: () => [],
  },
})

const isOpen = ref(true)
const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']

const goldenSet = computed(() => props.goldenDays instanceof Set ? props.goldenDays : new Set(props.goldenDays))

function keyForCell(monthIndex, weekIndex, dayIndex) {
  const date = new Date(new Date().getFullYear(), monthIndex, 1 + (weekIndex * 7) + dayIndex)
  return date.toISOString().slice(0, 10)
}

function weeksInMonth(monthIndex) {
  const date = new Date(new Date().getFullYear(), monthIndex + 1, 0)
  return Math.ceil((date.getDate() + new Date(date.getFullYear(), monthIndex, 1).getDay()) / 7)
}
</script>

<template>
  <VCard class="golden-path-card">
    <VCardText>
      <div class="d-flex align-center justify-space-between gap-3" :class="{ 'mb-4': isOpen }">
        <div class="d-flex align-center gap-2">
          <VBtn class="golden-collapse" icon size="x-small" variant="text" @click="isOpen = !isOpen">
            <VIcon :icon="isOpen ? 'mdi-chevron-down' : 'mdi-chevron-right'" size="16" />
          </VBtn>
          <VIcon icon="mdi-trophy" color="warning" />
          <div>
            <div class="text-overline text-warning mb-0">{{ $t('dashboard.weeklyReview.goldenOverline') }}</div>
            <div class="text-subtitle-2 font-weight-semibold">{{ $t('dashboard.weeklyReview.goldenPath') }}</div>
            <div class="text-caption text-medium-emphasis">
              {{ $t('weekCanvas.goldenPath.subtitle', { count: goldenSet.size }) }}
            </div>
          </div>
        </div>
        <VChip size="small" color="warning" variant="tonal">
          {{ goldenSet.size }}
        </VChip>
      </div>

      <VExpandTransition>
        <div v-show="isOpen" class="golden-path-scroll">
          <div v-for="(month, monthIndex) in months" :key="month" class="golden-month">
            <div class="text-caption text-medium-emphasis text-center mb-2">{{ month }}</div>
            <div class="d-flex gap-1">
              <div v-for="weekIndex in weeksInMonth(monthIndex)" :key="weekIndex" class="d-flex flex-column gap-1">
                <div
                  v-for="dayIndex in 7"
                  :key="dayIndex"
                  class="golden-cell"
                  :class="{ 'golden-cell--active': goldenSet.has(keyForCell(monthIndex, weekIndex - 1, dayIndex - 1)) }"
                  :title="keyForCell(monthIndex, weekIndex - 1, dayIndex - 1)"
                />
              </div>
            </div>
          </div>
        </div>
      </VExpandTransition>
    </VCardText>
  </VCard>
</template>

<style scoped lang="scss">
.golden-path-card {
  border: 1px solid rgba(var(--v-theme-warning), 0.18);
  border-radius: 18px;
  background:
    linear-gradient(180deg, rgba(var(--v-theme-surface), 0.78), rgba(var(--v-theme-surface), 0.56)),
    rgba(8, 12, 20, 0.74);
  backdrop-filter: blur(18px) saturate(1.2);
}

.golden-collapse {
  opacity: 0.56;
  transition: opacity 0.18s ease, background 0.18s ease;

  &:hover {
    opacity: 1;
    background: rgba(var(--v-theme-warning), 0.08);
  }
}

.golden-path-scroll {
  display: flex;
  gap: 16px;
  overflow-x: auto;
  padding-block-end: 4px;
}

.golden-month {
  flex: 0 0 auto;
}

.golden-cell {
  inline-size: 9px;
  block-size: 9px;
  border-radius: 2px;
  background: rgba(var(--v-theme-on-surface), 0.08);
  transition: transform 0.2s ease, background 0.2s ease;
}

.golden-cell--active {
  background: rgb(var(--v-theme-warning));
  box-shadow: 0 0 10px rgba(var(--v-theme-warning), 0.6);
  transform: scale(1.05);
}
</style>